import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

// Import images from assets
import image1 from "../assets/images/image1.jpg"
import image2 from "../assets/images/image2.jpg"
import image3 from "../assets/images/image3.jpg"
import image4 from "../assets/images/image4.jpg"

const slides = [
  {
    id: 1,
    title: "Innovation at Scale",
    description: "Building the future with cutting-edge technology and creative solutions",
    image: image1,
    color: "from-blue-500 to-cyan-500",
  },
  {
    id: 2,
    title: "Global Community",
    description: "Connecting developers and creators from around the world",
    image: image2,
    color: "from-purple-500 to-pink-500",
  },
  {
    id: 3,
    title: "Seamless Experience",
    description: "Intuitive design meets powerful functionality",
    image: image3,
    color: "from-orange-500 to-red-500",
  },
  {
    id: 4,
    title: "Future Ready",
    description: "Embracing tomorrow's technology today",
    image: image4,
    color: "from-green-500 to-emerald-500",
  },
]

export default function Carousel() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (isPaused) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length)
    }, 4000) // Auto-scroll every 4 seconds

    return () => clearInterval(interval)
  }, [isPaused])

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length)
  }

  const goToSlide = (index) => {
    setCurrentIndex(index)
  }

  return (
    <div className="w-full bg-slate-950 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Discover Our <span className="text-blue-400">Vision</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Explore the innovative solutions and experiences we create for our community
          </p>
        </div>

        {/* Main Carousel Container */}
        <div
          className="relative w-full h-[300px] md:h-[500px] lg:h-[700px] rounded-2xl overflow-hidden shadow-2xl border border-slate-800"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Slides */}
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentIndex ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = image1
                }}
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent flex flex-col justify-end p-6 md:p-8 lg:p-10">
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-3">
                  {slide.title}
                </h3>
                <p className="text-sm md:text-base lg:text-lg text-gray-200 max-w-2xl leading-relaxed">
                  {slide.description}
                </p>
              </div>
            </div>
          ))}

          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-slate-900/60 hover:bg-slate-800/80 text-white p-3 md:p-4 rounded-full transition-transform duration-200 backdrop-blur-sm border border-slate-700 hover:border-blue-400"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} className="md:w-6 md:h-6" />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-slate-900/60 hover:bg-slate-800/80 text-white p-3 md:p-4 rounded-full transition-transform duration-200 backdrop-blur-sm border border-slate-700 hover:border-blue-400"
            aria-label="Next slide"
          >
            <ChevronRight size={20} className="md:w-6 md:h-6" />
          </button>
        </div>

        {/* Indicators */}
        <div className="flex justify-center gap-3 mt-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-transform duration-300 rounded-full focus:outline-none ${
                index === currentIndex
                  ? "bg-blue-400 w-8 h-3"
                  : "bg-slate-600 w-3 h-3 hover:bg-slate-500 hover:scale-125"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Slide Counter */}
        <div className="flex justify-center mt-6">
          <div className="text-gray-400">
            <p className="text-sm">
              {currentIndex + 1} / {slides.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
